// src/components/dashboard/B2BSuggestionsComponent.jsx
// ------------------------------------------------------------------
// Dashboard component showing B2B category suggestions from clients
// ------------------------------------------------------------------

import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Chip,
    CircularProgress,
    IconButton,
    Tooltip,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Button,
    Menu,
    MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ComponentContainer = styled(Box)({
    backgroundColor: '#2a2a2a',
    borderRadius: '12px',
    padding: '1.5rem',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
});

const HeaderRow = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
});

const StatsRow = styled(Box)({
    display: 'flex',
    gap: '1rem',
    marginBottom: '1rem',
    flexWrap: 'wrap',
});

const StatBox = styled(Box)({
    backgroundColor: '#3a3a3a',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    minWidth: '80px',
    textAlign: 'center',
});

const getStatusColor = (status) => {
    switch (status) {
        case 'pending': return '#FFA726';
        case 'under_review': return '#42A5F5';
        case 'approved': return '#66BB6A';
        case 'rejected': return '#EF5350';
        case 'implemented': return '#AB47BC';
        default: return '#888';
    }
};

const getStatusIcon = (status) => {
    switch (status) {
        case 'pending': return <PendingIcon sx={{ fontSize: 16 }} />;
        case 'approved': return <CheckCircleIcon sx={{ fontSize: 16 }} />;
        case 'rejected': return <CancelIcon sx={{ fontSize: 16 }} />;
        default: return null;
    }
};

const B2BSuggestionsComponent = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedSuggestion, setSelectedSuggestion] = useState(null);

    const fetchSuggestions = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE}/b2b-suggestions/summary`);
            if (response.data.success) {
                setData(response.data.summary);
            }
        } catch (err) {
            console.error('[B2BSuggestions] Error:', err);
            setError('Failed to load suggestions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSuggestions();
    }, []);

    const handleMenuOpen = (event, suggestion) => {
        setMenuAnchor(event.currentTarget);
        setSelectedSuggestion(suggestion);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setSelectedSuggestion(null);
    };

    const handleStatusUpdate = async (newStatus) => {
        if (!selectedSuggestion) return;

        try {
            await axios.patch(`${API_BASE}/b2b-suggestions/${selectedSuggestion._id}`, {
                status: newStatus,
                reviewedBy: 'Admin'
            });
            fetchSuggestions(); // Refresh data
        } catch (err) {
            console.error('[B2BSuggestions] Update error:', err);
        }
        handleMenuClose();
    };

    if (loading) {
        return (
            <ComponentContainer>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress sx={{ color: '#C63D57' }} />
                </Box>
            </ComponentContainer>
        );
    }

    if (error) {
        return (
            <ComponentContainer>
                <Typography color="error">{error}</Typography>
                <Button onClick={fetchSuggestions} sx={{ mt: 2 }}>Retry</Button>
            </ComponentContainer>
        );
    }

    return (
        <ComponentContainer>
            <HeaderRow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LightbulbIcon sx={{ color: '#FFA726' }} />
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                        B2B Category Requests
                    </Typography>
                </Box>
                <Tooltip title="Refresh">
                    <IconButton onClick={fetchSuggestions} sx={{ color: '#888' }}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </HeaderRow>

            {/* Stats Row */}
            <StatsRow>
                <StatBox>
                    <Typography sx={{ color: '#FFA726', fontSize: '1.5rem', fontWeight: 700 }}>
                        {data?.pending || 0}
                    </Typography>
                    <Typography sx={{ color: '#888', fontSize: '0.75rem' }}>Pending</Typography>
                </StatBox>
                <StatBox>
                    <Typography sx={{ color: '#66BB6A', fontSize: '1.5rem', fontWeight: 700 }}>
                        {data?.approved || 0}
                    </Typography>
                    <Typography sx={{ color: '#888', fontSize: '0.75rem' }}>Approved</Typography>
                </StatBox>
                <StatBox>
                    <Typography sx={{ color: '#AB47BC', fontSize: '1.5rem', fontWeight: 700 }}>
                        {data?.implemented || 0}
                    </Typography>
                    <Typography sx={{ color: '#888', fontSize: '0.75rem' }}>Implemented</Typography>
                </StatBox>
                <StatBox>
                    <Typography sx={{ color: '#42A5F5', fontSize: '1.5rem', fontWeight: 700 }}>
                        {data?.recentCount || 0}
                    </Typography>
                    <Typography sx={{ color: '#888', fontSize: '0.75rem' }}>Last 7 Days</Typography>
                </StatBox>
            </StatsRow>

            <Divider sx={{ borderColor: '#444', my: 1 }} />

            {/* Top Requested Categories */}
            {data?.topCategories?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <TrendingUpIcon sx={{ color: '#12c099', fontSize: 18 }} />
                        <Typography sx={{ color: '#888', fontSize: '0.8rem', fontWeight: 600 }}>
                            TOP REQUESTED
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {data.topCategories.map((cat, idx) => (
                            <Chip
                                key={idx}
                                label={`${cat.category} (${cat.count})`}
                                size="small"
                                sx={{
                                    bgcolor: idx === 0 ? 'rgba(198,61,87,0.2)' : 'rgba(255,255,255,0.1)',
                                    color: idx === 0 ? '#C63D57' : '#ccc',
                                    fontWeight: idx === 0 ? 700 : 400,
                                    textTransform: 'capitalize',
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Recent Pending List */}
            <Typography sx={{ color: '#888', fontSize: '0.8rem', fontWeight: 600, mb: 1 }}>
                RECENT PENDING
            </Typography>

            {data?.recentPending?.length > 0 ? (
                <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
                    {data.recentPending.map((suggestion) => (
                        <ListItem
                            key={suggestion._id}
                            sx={{
                                bgcolor: '#333',
                                borderRadius: '8px',
                                mb: 0.5,
                                py: 1,
                            }}
                        >
                            <ListItemText
                                primary={
                                    <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.95rem' }}>
                                        {suggestion.suggestedCategory}
                                    </Typography>
                                }
                                secondary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                        <Typography sx={{ color: '#888', fontSize: '0.75rem' }}>
                                            {suggestion.organizationName || suggestion.submitterEmail || 'Anonymous'}
                                        </Typography>
                                        <Chip
                                            label={suggestion.source || 'onboarding'}
                                            size="small"
                                            sx={{
                                                height: 18,
                                                fontSize: '0.65rem',
                                                bgcolor: 'rgba(255,255,255,0.1)',
                                                color: '#888',
                                            }}
                                        />
                                    </Box>
                                }
                            />
                            <ListItemSecondaryAction>
                                <IconButton
                                    size="small"
                                    onClick={(e) => handleMenuOpen(e, suggestion)}
                                    sx={{ color: '#888' }}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography sx={{ color: '#666' }}>No pending suggestions</Typography>
                </Box>
            )}

            {/* Status Update Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: { bgcolor: '#333', color: '#fff' }
                }}
            >
                <MenuItem onClick={() => handleStatusUpdate('under_review')}>
                    Mark as Under Review
                </MenuItem>
                <MenuItem onClick={() => handleStatusUpdate('approved')}>
                    Approve
                </MenuItem>
                <MenuItem onClick={() => handleStatusUpdate('rejected')}>
                    Reject
                </MenuItem>
                <MenuItem onClick={() => handleStatusUpdate('implemented')}>
                    Mark as Implemented
                </MenuItem>
            </Menu>
        </ComponentContainer>
    );
};

export default B2BSuggestionsComponent;
